import CustomCard from '../../../components/card/CustomCard'
import CustomCardHeader from '../../../components/card/CustomCardHeader'
import CardBody from '../../../components/card/CardBody'
import TableItemLoader from '../../../components/TableItemLoader'
import CustomTable, {
  TableHeader,
  TableRow,
} from '../../../components/table/CustomTable'
import CardFooter from '../../../components/card/CardFooter'
import CustomButton, { OutlinedButton } from '../../../components/CustomButton'
import CustomIcon from '../../../components/CustomIcon'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import React, { useEffect, useState } from 'react'
import { fetchCampaignReports } from '../../../redux/slices/campaignReportingSlice'
import LabeledSelect from '../../../components/selects/LabeledSelect'
import DatePicker from '../../../components/datepicker/DatePicker'
import { RadioGroup, RadioItem } from '../../../components/form/RadioGroup'
import CampaignReportItem from './CampaignReportItem'
import FormLabel from '../../../components/form/FormLabel'
import CustomTableItemLoader from '../../../components/CustomTableItemLoader'

const frequencyOptions = [
  {
    value: 'DAILY',
    label: 'Daily',
  },
  {
    value: 'WEEKLY',
    label: 'Weekly',
  },
  {
    value: 'MONTHLY',
    label: 'Monthly',
  },
]

const statusOptions = [
  {
    value: 'BOOKED',
    label: 'Booked',
  },
  {
    value: 'ASSIGNED',
    label: 'Assigned',
  },
  {
    value: 'COMPLETED',
    label: 'Completed',
  },
  {
    value: 'CANCELLED',
    label: 'Cancelled',
  },
]

const searchByType = ['Frequency', 'Date']

const CampaignReportTable = () => {
  const searchParam = useLocation().search

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [searchBy, setSearchBy] = useState(searchByType[0])
  const [selectedStatus, setSelectedStatus] = useState()
  const [selectedFrequency, setSelectedFrequency] = useState()
  const [selectedFromDate, setSelectedFromDate] = useState()
  const [selectedToDate, setSelectedToDate] = useState()

  const { hasPrev, hasNext, currentPage, loading, campaigns, error } =
    useSelector((state) => state.campaignReporting)

  useEffect((e) => {
    const param = new URLSearchParams(searchParam)
    const page = param.get('page')
    const frequency = param.get('frequency')
    const status = param.get('status')
    const from = param.get('from')
    const to = param.get('to')

    const selectedFrequencyOption = frequencyOptions.find(
      (f) => f.value === frequency?.toUpperCase(),
    )
    const selectedStatusOption = statusOptions.find(
      (s) => s.value === status?.toUpperCase(),
    )

    fetchCampaignsAction(
      page ?? 1,
      selectedFrequencyOption ?? selectedFrequency,
      selectedStatusOption ?? selectedStatus,
      from ?? selectedFromDate,
      to ?? selectedToDate,
    )
  }, [])

  function fetchCampaignsAction(page, frequency, status, from, to) {
    if (frequency !== undefined) {
      setSelectedFrequency(frequency)
    }

    if (status !== undefined) {
      setSelectedStatus(status)
    }

    if (from !== undefined || null) {
      setSelectedFromDate(from)
    }

    if (to !== undefined || null) {
      setSelectedToDate(to)
    }

    if (frequency) {
      setSearchBy(searchByType[0])
    }
    if (from && to) {
      setSearchBy(searchByType[1])
    }

    const f = frequency ?? selectedFrequency
    const s = status ?? selectedStatus
    const fromD = from ?? selectedFromDate
    const toD = to ?? selectedToDate

    dispatch(
      fetchCampaignReports({
        page: page,
        status: s?.value,
        frequency: f?.value,
        fromDate: fromD,
        toDate: toD,
      }),
    )
    navigate({
      search: `?page=${page}${f !== undefined ? `&frequency=${f?.value.toLowerCase()}` : ''
        }${s !== undefined ? `&status=${s?.value.toLowerCase()}` : ''}${fromD !== undefined || null ? `&from=${fromD}` : ''
        }${toD !== undefined || null ? `&to=${toD}` : ''}`,
    })
  }

  return (
    <CustomCard>
      <CustomCardHeader color="blue" contentPosition="left">
        <h2 className="text-white text-2xl">Campaign Report</h2>
      </CustomCardHeader>
      <CardBody>
        <div className="border rounded-lg pt-3 pr-4 pl-4 mb-2">
          <div className="flex items-center border-b-2 border-dashed mb-4">
            <FormLabel label="Search By: " className="mr-4" />
            <RadioGroup>
              {' '}
              {searchByType.map((type) => {
                return (
                  <RadioItem
                    className="text-sm"
                    key={type}
                    name="searchType"
                    label={type}
                    value={searchBy}
                    isChecked={searchBy === type}
                    onChanged={() => {
                      setSelectedFrequency(undefined)
                      setSelectedFromDate(undefined)
                      setSelectedToDate(undefined)
                      setSelectedStatus(undefined)
                      setSearchBy(type)
                    }}
                  />
                )
              })}{' '}
            </RadioGroup>
          </div>
          <div className="flex items-end space-x-4">
            <div className="flex w-full space-x-4">
              {searchBy === searchByType[0] ? (
                <LabeledSelect
                  label="Frequency"
                  placeholder="Select frequency"
                  options={frequencyOptions}
                  value={frequencyOptions.find((f) => f === selectedFrequency)}
                  onChange={setSelectedFrequency}
                />
              ) : (
                <>
                  <DatePicker
                    name="Start Date"
                    selectedDate={selectedFromDate}
                    onChange={(date) => {
                      setSelectedFromDate(date)
                    }}
                  />
                  <DatePicker
                    name="End Date"
                    selectedDate={selectedToDate}
                    onChange={(date) => {
                      setSelectedToDate(date)
                    }}
                  />
                </>
              )}

              <LabeledSelect
                label="Campaign Status"
                placeholder="Select Status"
                options={statusOptions}
                value={statusOptions.find((s) => s === selectedStatus)}
                onChange={setSelectedStatus}
              />
            </div>
            <div className="mb-4">
              <CustomButton
                onClick={() => fetchCampaignsAction(currentPage)}
                className="px-8"
              >
                Search
              </CustomButton>
            </div>
          </div>
        </div>
        <CustomTable>
          <thead>
            <TableRow>
              <TableHeader>Campaign</TableHeader>
              <TableHeader>Booked By</TableHeader>
              <TableHeader>Appointment on</TableHeader>
              <TableHeader>Payment Status</TableHeader>
              <TableHeader className="text-right">Amount</TableHeader>
              <TableHeader>Status</TableHeader>
            </TableRow>
          </thead>

          {loading || error.length > 0 || campaigns.length === 0 ? (
            <CustomTableItemLoader
              colSpan={6}
              loading={loading}
              error={error}
              isEmpty={campaigns.length === 0}
              retry={() => fetchCampaignsAction(currentPage)}
            />
          ) : (
            <tbody>
              {campaigns.map((campaign) => (
                <CampaignReportItem key={campaign.id} campaignItem={campaign} />
              ))}
            </tbody>
          )}
        </CustomTable>
      </CardBody>
      {!(loading || error.length > 0) && campaigns.length > 0 && (
        <CardFooter>
          <div className="flex justify-end space-x-4">
            <CustomButton
              className="rounded"
              onClick={() => fetchCampaignsAction(currentPage - 1)}
              disabled={!hasPrev}
            >
              <CustomIcon className="mt-2 w-4" icon="keyboard_arrow_left" />
            </CustomButton>

            <CustomButton
              className="rounded"
              onClick={() => fetchCampaignsAction(currentPage + 1)}
              disabled={!hasNext}
            >
              <CustomIcon className="mt-2 w-4" icon="keyboard_arrow_right" />
            </CustomButton>
          </div>
        </CardFooter>
      )}
    </CustomCard>
  )
}

export default CampaignReportTable
